import { useEffect, useState } from 'react';

import { getAdminDocsIndex, getAdminDocsResource, getAtlasHtml } from '../../services/adminDocs';
import { onSessionChange } from '../../utils/session';

// The portal reads from two places, because it has two audiences:
//
//   static   public/docs-portal/ — the guides for everyone who signs in. Plain
//            files on the hosting origin, so NOT through src/api/client.js (no
//            token, a different origin in development) — and, being static,
//            public whatever the UI hides. Nothing admin-only lives there.
//   admin    GET /api/admin/docs/* — the change log, the line counts, the
//            atlases and the operator guides, behind assertAdmin.
//
// One promise per resource for the life of the page: both change only with a
// deploy, and the change log is the better part of a megabyte.
const ADMIN_RESOURCES = new Set(['changelog', 'changelog-files', 'loc', 'atlases', 'admin-guides', 'admin-guides-body']);
const ADMIN_NAMES = { 'admin-guides': 'guides', 'admin-guides-body': 'guides-body' };

const cache = new Map();
// The admin half is an ADMIN's answer: an account that signs in after one on
// this tab must not be handed it from memory.
onSessionChange(() => cache.clear());

export const NOT_PUBLISHED = 'This part of the portal is not published on this deployment.';

function fetchStatic(name) {
	const url = `${process.env.PUBLIC_URL || ''}/docs-portal/${name}.json`;
	return fetch(url).then(response => {
		if (!response.ok) throw new Error(`${name}.json answered ${response.status}`);
		// A static file that is not in the deploy does not 404: the hosting
		// rewrite answers every unknown path with index.html and a 200. Say
		// what happened rather than "Unexpected token '<'".
		const type = response.headers?.get?.('content-type') || '';
		if (type && !type.includes('json')) throw new Error(NOT_PUBLISHED);
		return response.json();
	});
}

function fetchResource(name) {
	if (name === 'admin-index') return getAdminDocsIndex();
	if (name.startsWith('atlas:')) return getAtlasHtml(name.slice(6));
	if (ADMIN_RESOURCES.has(name)) return getAdminDocsResource(ADMIN_NAMES[name] || name);
	return fetchStatic(name);
}

export function loadDocsData(name) {
	if (!cache.has(name)) {
		cache.set(name, fetchResource(name).catch(error => {
			cache.delete(name);
			throw error;
		}));
	}
	return cache.get(name);
}

/** Tests seed the cache instead of stubbing fetch and the API. */
export function primeDocsData(name, value) {
	cache.set(name, Promise.resolve(value));
}

export function clearDocsData() {
	cache.clear();
}

/** The API's own words for a refusal, where it gave any. */
export const docsErrorText = error => error?.response?.data?.message || error?.message || 'Unknown error';

/** { data, error, loading } for one resource. */
export default function useDocsData(name, { enabled = true } = {}) {
	const [state, setState] = useState({ data: null, error: null, loading: enabled });

	useEffect(() => {
		if (!enabled) {
			setState({ data: null, error: null, loading: false });
			return undefined;
		}
		let live = true;
		setState(previous => ({ ...previous, loading: true }));
		loadDocsData(name).then(
			data => live && setState({ data, error: null, loading: false }),
			error => live && setState({ data: null, error, loading: false })
		);
		return () => { live = false; };
	}, [name, enabled]);

	return state;
}

// Sections in the order the build lists them, whichever source they came from.
const SECTION_ORDER = ['getting-started', 'collections', 'discover', 'cards-and-elements', 'data-types', 'items', 'finding-and-lists', 'reports', 'sharing-and-export', 'automations-and-notifications', 'account', 'admin', 'api-and-integrations'];
const rank = key => {
	const at = SECTION_ORDER.indexOf(key);
	return at === -1 ? SECTION_ORDER.length : at;
};

export function mergeGuides(open, operator) {
	if (!open) return null;
	if (!operator) return open;
	const guides = [...open.guides, ...operator.guides];
	const keys = [...new Set([...open.sections, ...operator.sections].map(section => section.key))];
	const sections = keys
		.map(key => ({
			...(open.sections.find(section => section.key === key) || operator.sections.find(section => section.key === key)),
			count: guides.filter(guide => guide.section === key).length,
		}))
		.sort((a, b) => rank(a.key) - rank(b.key));
	guides.sort((a, b) => rank(a.section) - rank(b.section) || a.order - b.order || a.slug.localeCompare(b.slug));
	return { sections, guides };
}

/**
 * The guide index this person may read: the public guides, plus — for an admin
 * — the operator guides from the API. If the API cannot answer (not deployed
 * yet, or down) an admin still gets the public guides: `operatorError` says
 * why the rest is missing, and nothing else on the page depends on it.
 */
export function useGuides(admin) {
	const open = useDocsData('guides');
	const operator = useDocsData('admin-guides', { enabled: admin });
	const [merged, setMerged] = useState(null);

	// Merged once per pair of payloads, not per render: search caches its
	// folded text on the guide objects, and a fresh array every render would
	// throw that away.
	useEffect(() => {
		setMerged(mergeGuides(open.data, admin ? operator.data : null));
	}, [open.data, operator.data, admin]);

	return {
		data: merged,
		loading: open.loading || (admin && operator.loading) || (Boolean(open.data) && !merged),
		error: open.error,
		operatorError: admin ? operator.error : null,
	};
}

/** Where one guide's prose lives: its section's static file, or the API's operator map. */
export const bodySourceFor = guide => (guide.audience === 'admin' ? 'admin-guides-body' : `guides-body/${guide.section}`);

/** Every readable guide's prose as one { id: markdown } map — what full-text search reads. */
export function loadGuideBodies(guides) {
	const sources = [...new Set(guides.map(bodySourceFor))];
	// A source that fails costs the search that source, not the search.
	return Promise.all(sources.map(source => loadDocsData(source).catch(() => ({}))))
		.then(maps => Object.assign({}, ...maps));
}
